import React from 'react'
import _ from 'lodash'
import FragmentService, {
  ThumbnailBlob,
} from 'fragmentarium/application/FragmentService'
import withData from 'http/withData'
import { QueryItem } from 'query/QueryResult'
import { Col, Container, Row } from 'react-bootstrap'
import { Fragment } from 'fragmentarium/domain/fragment'
import RenderFragmentLines from 'dictionary/ui/search/RenderFragmentLines'
import FragmentLink, { createFragmentUrl } from 'fragmentarium/ui/FragmentLink'
import { Genres } from 'fragmentarium/domain/Genres'
import ReferenceList from 'bibliography/ui/ReferenceList'
import 'fragmentarium/ui/search/FragmentariumSearchResult.sass'
import DateDisplay from 'chronology/ui/DateDisplay'
import { ProjectList } from 'fragmentarium/ui/info/ResearchProjects'
import { RecordList } from 'fragmentarium/ui/info/Record'
import ErrorBoundary from 'common/errors/ErrorBoundary'
import { ThumbnailImage } from 'common/ui/BlobImage'
import DossiersService from 'dossiers/application/DossiersService'
import useNearViewport from 'common/hooks/useNearViewport'
import FragmentDossierRecordsDisplay from 'dossiers/ui/DossiersDisplay'
import SummaryThumbnail from 'fragmentarium/ui/search/SummaryThumbnail'
import {
  getLatestTransliterationRecord,
  hasRenderReadyFragment,
  hasUnsupportedFragmentCardSummary,
} from 'query/queryItemRenderReady'
import UnavailableFragmentCard from 'fragmentarium/ui/search/UnavailableFragmentCard'

function GenresDisplay({ genres }: { genres: Genres }): JSX.Element {
  return (
    <ul>
      {genres.genres.map((genreItem, index) => {
        return (
          <ul key={index}>
            <small>{genreItem.toString()}</small>
          </ul>
        )
      })}
    </ul>
  )
}

export type FragmentLinesService = Pick<
  FragmentService,
  'find' | 'findThumbnail'
>

const FragmentThumbnail = withData<
  { fragment: Fragment },
  { fragmentService: FragmentLinesService },
  ThumbnailBlob
>(
  ({ data, fragment }) => {
    return data.blob ? (
      <ThumbnailImage
        photo={data.blob}
        alt={`Preview of ${fragment.number}`}
        url={createFragmentUrl(fragment.number)}
      />
    ) : (
      <></>
    )
  },
  ({ fragment, fragmentService }) =>
    fragmentService.findThumbnail(fragment, 'small'),
)

function TransliterationRecord({
  fragment,
  className,
}: {
  fragment: Fragment
  className?: string
}): JSX.Element {
  const latestRecord = getLatestTransliterationRecord(fragment)
  return (
    <RecordList
      record={latestRecord ? [latestRecord] : []}
      className={className}
    />
  )
}

function ResponsiveCol({ ...props }): JSX.Element {
  return <Col xs={12} sm={4} {...props}></Col>
}

type FragmentLinesProps = {
  queryLemmas?: readonly string[]
  queryItem: QueryItem
  linesToShow: number
  includeLatestRecord?: boolean
  fragmentService: FragmentLinesService
  dossiersService: DossiersService
  active?: number
}

function FragmentLinesContent({
  fragment,
  queryItem,
  queryLemmas,
  linesToShow,
  includeLatestRecord,
  fragmentService,
  dossiersService,
}: FragmentLinesProps & { fragment: Fragment }): JSX.Element {
  const { containerRef: thumbnailContainerRef, isNearViewport } =
    useNearViewport()
  const script = fragment.script.period.abbreviation
    ? ` (${fragment.script.period.abbreviation})`
    : ''
  const usesSummaryThumbnail = 'thumbnailPath' in queryItem

  return (
    <Container>
      <Row className={'fragment-result__header'}>
        <ResponsiveCol>
          <h4 className={'fragment-result__fragment-number'}>
            <FragmentLink number={fragment.number}>
              {fragment.number}
            </FragmentLink>
            {script}
          </h4>
          <div className="fragment-result__archaeology-info">
            <small>
              <p>
                {fragment.accession && 'Accession no.: '}
                {fragment.accession}
              </p>
              <p>
                {fragment.archaeology?.excavationNumber && 'Excavation no.: '}
                {fragment.archaeology?.excavationNumber}
              </p>
              <p>
                {fragment.archaeology?.site?.name && 'Provenance: '}
                {fragment.archaeology?.site?.name}
              </p>
              <FragmentDossierRecordsDisplay
                dossiersService={dossiersService}
                fragment={fragment}
              />
            </small>
          </div>
          <ProjectList projects={fragment.projects} />
        </ResponsiveCol>
        <ResponsiveCol className={'text-secondary fragment-result__genre'}>
          <GenresDisplay genres={fragment.genres} />
        </ResponsiveCol>
        <ResponsiveCol className={'fragment-result__record'}>
          {includeLatestRecord && <TransliterationRecord fragment={fragment} />}
        </ResponsiveCol>
      </Row>
      {fragment?.date && (
        <Row>
          <ResponsiveCol>
            <DateDisplay date={fragment.date} />
          </ResponsiveCol>
        </Row>
      )}
      <Row>
        <ResponsiveCol className={'text-secondary'}>
          <small>
            <ReferenceList references={fragment.references} />
          </small>
        </ResponsiveCol>
        <ResponsiveCol className={'mt-4 mb-4 mt-sm-0 mb-sm-0'}>
          <RenderFragmentLines
            fragment={fragment}
            linesToShow={linesToShow}
            totalLines={queryItem.matchCount}
            lemmaIds={queryLemmas}
          />
        </ResponsiveCol>
        <ResponsiveCol className={'fragment-result__preview'}>
          <ErrorBoundary>
            <div ref={thumbnailContainerRef}>
              {fragment.hasPhoto && isNearViewport && (
                <>
                  {usesSummaryThumbnail ? (
                    <SummaryThumbnail
                      fragmentNumber={fragment.number}
                      thumbnailPath={queryItem.thumbnailPath ?? null}
                    />
                  ) : (
                    <FragmentThumbnail
                      fragmentService={fragmentService}
                      fragment={fragment}
                    />
                  )}
                </>
              )}
            </div>
          </ErrorBoundary>
        </ResponsiveCol>
      </Row>
      <hr />
    </Container>
  )
}

const HydratedFragmentLines = withData<
  Omit<FragmentLinesProps, 'active'>,
  {
    active?: number
  },
  Fragment
>(
  ({ data: fragment, ...props }): JSX.Element => (
    <FragmentLinesContent fragment={fragment} {...props} />
  ),
  ({ fragmentService, queryItem, linesToShow }) => {
    const excludeLines = _.isEmpty(queryItem.matchingLines)
    return fragmentService.find(
      queryItem.museumNumber,
      _.take(queryItem.matchingLines, linesToShow),
      excludeLines,
    )
  },
  {
    watch: ({ active }) => [active],
  },
)

function FragmentLinesCard(props: FragmentLinesProps): JSX.Element {
  const { queryItem, includeLatestRecord } = props

  if (hasRenderReadyFragment(queryItem, { includeLatestRecord })) {
    return <FragmentLinesContent fragment={queryItem.fragment} {...props} />
  }

  if (hasUnsupportedFragmentCardSummary(queryItem)) {
    return <UnavailableFragmentCard museumNumber={queryItem.museumNumber} />
  }

  return <HydratedFragmentLines {...props} />
}

export function FragmentLines(props: FragmentLinesProps): JSX.Element {
  return (
    <ErrorBoundary
      fallback={
        <UnavailableFragmentCard museumNumber={props.queryItem.museumNumber} />
      }
    >
      <FragmentLinesCard {...props} />
    </ErrorBoundary>
  )
}
